import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Users, Plus, Trash2, Loader2, Shield, User as UserIcon } from "lucide-react";
import { getUsers, createUser, updateUser, deleteUser } from "../../../services/dataService";
import { PageHeader } from "../../PageHeader";
import { StatusBadge } from "../../StatusBadge";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { useAuth } from "../../../context/AuthContext";
import { formatApiErrorDetail } from "../../../lib/api";
import { formatDate } from "../../../lib/format";
import { toast } from "sonner";

export function UsersSettings() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user" });

  const { data: users = [], isLoading, error } = useQuery({ queryKey: ["users"], queryFn: getUsers, retry: false });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["users"] });

  const createMut = useMutation({
    mutationFn: () => createUser(form),
    onSuccess: () => { invalidate(); toast.success("User created"); setOpen(false); setForm({ name: "", email: "", password: "", role: "user" }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create user"),
  });
  const roleMut = useMutation({
    mutationFn: ({ id, role }) => updateUser(id, { role }),
    onSuccess: () => { invalidate(); toast.success("Role updated"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const delMut = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => { invalidate(); toast.success("User removed"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete"),
  });

  if (error) {
    return (
      <div>
        <PageHeader title="Users" icon={Users} description="Manage team access." />
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-700" data-testid="users-forbidden">
          You need administrator access to manage users.
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Users" icon={Users} description="Manage team members and their access roles (Admin / Financial Clerk).">
        <Button onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800 dark:bg-blue-600 text-white font-semibold" data-testid="add-user-button">
          <Plus className="h-4 w-4 mr-2" /> Add User
        </Button>
      </PageHeader>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
      ) : (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden shadow-sm">
          <Table data-testid="users-table">
            <TableHeader>
              <TableRow className="bg-slate-50/80 dark:bg-slate-800/60">
                <TableHead className="text-xs uppercase tracking-wider">Name</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Email</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Role</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Added</TableHead>
                <TableHead className="w-24 text-right text-xs uppercase tracking-wider">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-medium flex items-center gap-2">
                    {u.role === "admin" ? <Shield className="h-4 w-4 text-blue-600" /> : <UserIcon className="h-4 w-4 text-slate-400" />}
                    {u.name}
                  </TableCell>
                  <TableCell className="text-slate-600 dark:text-slate-300">{u.email}</TableCell>
                  <TableCell>
                    <Select value={u.role} onValueChange={(role) => roleMut.mutate({ id: u.id, role })} disabled={u.id === me?.id}>
                      <SelectTrigger className="w-36 h-8" data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Administrator</SelectItem>
                        <SelectItem value="user">Financial Clerk</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-slate-500 text-sm">{formatDate(u.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-rose-600" disabled={u.id === me?.id} onClick={() => delMut.mutate(u.id)} data-testid={`delete-user-${u.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-user-modal">
          <DialogHeader><DialogTitle>Add Team Member</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></div>
            <div className="space-y-2"><Label>Temporary password</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" /></div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(role) => setForm({ ...form, role })}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrator</SelectItem>
                  <SelectItem value="user">Financial Clerk</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => createMut.mutate()} disabled={createMut.isPending || !form.email || !form.password || !form.name} className="bg-slate-900 dark:bg-blue-600 text-white" data-testid="save-user-button">
              {createMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
